<script setup>
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Button, Card, Input, Select, Textarea, Badge } from '@drama/ui'
import ListToolbar from '../components/ListToolbar.vue'
import WorkSyncSetup from '../components/WorkSyncSetup.vue'
import { useListView } from '../list-view.js'
import { api, upload, download as downloadFile, currentGuild, session, agentsIn, inGuild } from '../api.js'

const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()
const rows = ref([])
const next_before = ref(null)
const detail = ref(null)
const synchronization = ref(null)
const children = ref([])
const child_cursor = ref(null)
const members = ref([])
const notices = ref([])
const balance = ref(null)
const busy = ref(false)
let refresh_requested = false
const error = ref('')
const view = ref('mine')
const summary = ref('')
const artifacts = ref([])
const link = ref('')
const reason = ref('')
const chosen_owner = ref('')
const chosen_executor = ref('')
const kr_options = ref([])
const kr = ref('')
const task = computed(() => detail.value === null ? null : detail.value.task)
const mine = computed(() => task.value !== null && session.user !== null && task.value.holder_user_id === session.user.id)
const publisher = computed(() => task.value !== null && session.user !== null && task.value.publisher_user_id === session.user.id)
const ownerOptions = computed(() => [{ value: '', label: t('okr.unassigned') }, ...members.value.filter((item) => item.can_assign === 1).map((item) => ({ value: item.user_id, label: item.handle }))])
const agentOptions = computed(() => [{ value: '', label: t('okr.byPerson') }, ...agentsIn(currentGuild.slug).filter((agent) => agent.status === 'active').map((agent) => ({ value: agent.id, label: agent.full_name }))])
const listView = useListView(rows, {
  text: (item) => item.title,
  filters: [{ key: 'status', label: t('okr.allStatuses'), options: ['open', 'claimed', 'submitted', 'rework', 'settled', 'cancelled'].map((value) => ({ value, label: t(`okr.status.${value}`) })), match: (item, value) => item.status === value }],
  sorts: [{ value: 'default', label: t('list.sort'), compare: () => 0 }, { value: 'reward', label: t('okr.sortReward'), compare: (a, b) => b.reward - a.reward }],
})
const date = (value) => new Date(value).toLocaleString(locale.value)
const person = (id) => id === null ? t('okr.unassigned') : members.value.find((item) => item.user_id === id).handle
const workPath = (suffix) => inGuild(`/api/voko/work${suffix}`)

async function perform(body) {
  if (busy.value) return
  busy.value = true
  error.value = ''
  try { await body() } catch (err) { error.value = err.message } finally {
    busy.value = false
    if (refresh_requested) { refresh_requested = false; await perform(refresh) }
  }
}
async function loadRows(more = false) {
  const suffix = `${view.value === 'mine' ? '/mine' : '/'}?${more && next_before.value !== null ? `before=${encodeURIComponent(next_before.value)}` : ''}`
  const result = await api('GET', workPath(suffix))
  rows.value = more ? [...rows.value, ...result.tasks] : result.tasks
  next_before.value = result.next_before
}
async function loadChildren(more = false) {
  const result = await api('GET', workPath(`/?parent_task_id=${encodeURIComponent(task.value.id)}${more && child_cursor.value !== null ? `&before=${encodeURIComponent(child_cursor.value)}` : ''}`))
  children.value = more ? [...children.value, ...result.tasks] : result.tasks
  child_cursor.value = result.next_before
}
async function loadDetail() {
  if (route.params.id === undefined) { detail.value = null; synchronization.value = null; return }
  const [next_detail, next_sync] = await Promise.all([
    api('GET', workPath(`/${route.params.id}`)), api('GET', workPath(`/${route.params.id}/sync`)),
  ])
  if (detail.value === null || detail.value.task.id !== next_detail.task.id) {
    summary.value = ''
    artifacts.value = []
    link.value = ''
    reason.value = ''
  } else if (detail.value.task.assignment_version !== next_detail.task.assignment_version) artifacts.value = []
  detail.value = next_detail
  synchronization.value = next_sync
  chosen_owner.value = task.value.holder_user_id === null ? '' : task.value.holder_user_id
  chosen_executor.value = task.value.executor_agent_id === null ? '' : task.value.executor_agent_id
  await loadChildren()
}
async function refresh() {
  if (currentGuild.slug === null || session.user === null) return
  const [people, inbox, wallet, treeList] = await Promise.all([
    api('GET', workPath('/members')), api('GET', workPath('/inbox')), api('GET', workPath('/wallet')), api('GET', inGuild('/api/voko/trees')),
  ])
  members.value = people.members
  notices.value = inbox.activities
  balance.value = wallet.wallet
  const options = []
  for (const entry of treeList.trees.filter((item) => item.kind === 'okr')) {
    const { tree } = await api('GET', inGuild(`/api/voko/trees/${entry.slug}`))
    const pending = [...tree.root.children]
    while (pending.length > 0) {
      const node = pending.pop()
      options.push({ value: `${tree.slug}:${node.id}`, label: `${tree.title} / ${node.id} ${node.title}` })
      pending.push(...node.children)
    }
  }
  kr_options.value = options
  await loadRows()
  await loadDetail()
}
function requestRefresh() {
  if (busy.value) refresh_requested = true
  else perform(refresh)
}
watch([() => currentGuild.slug, () => session.user === null ? null : session.user.id, () => route.params.id, view], requestRefresh, { immediate: true })
async function action(name, fields = {}) {
  await perform(async () => {
    await api('POST', workPath(`/${task.value.id}/actions`), { action: name, version: task.value.version, ...fields })
    if (name === 'submit') { summary.value = ''; artifacts.value = []; link.value = '' }
    if (name === 'reject') reason.value = ''
    await refresh()
  })
}
async function attach(event) {
  const file = event.target.files[0]
  if (file === undefined) return
  await perform(async () => {
    const result = await upload(workPath(`/${task.value.id}/files?filename=${encodeURIComponent(file.name)}&version=${task.value.version}`), file)
    artifacts.value.push({ type: 'file', file_id: result.file.id })
  })
  event.target.value = ''
}
async function download(file_id) {
  await perform(async () => {
    await downloadFile(workPath(`/${task.value.id}/files/${file_id}`), file_id)
  })
}
async function synchronize(action, fields = {}) {
  await perform(async () => {
    await api('POST', workPath(`/${task.value.id}/sync/${action}`), fields)
    await refresh()
  })
}
async function claimKr() {
  if (kr.value === '') return
  await perform(async () => {
    const [tree_slug, node_id] = kr.value.split(':')
    const result = await api('POST', workPath('/claim-kr'), { tree_slug, node_id })
    await router.push(`/work/${result.task.id}`)
    await refresh()
  })
}
</script>

<template>
  <section class="work-page" :aria-busy="busy">
  <header class="work-header"><div><h1>{{ t('okr.title') }}</h1><p>{{ t('okr.subtitle') }}</p></div><Button v-if="currentGuild.slug !== null" @click="router.push('/work/new')">{{ t('okr.newTask') }}</Button></header>
  <p v-if="error" role="alert">{{ error }} <Button variant="ghost" @click="perform(refresh)">{{ t('okr.refresh') }}</Button></p>
  <p v-if="currentGuild.slug === null">{{ t('okr.chooseGuild') }}</p>
  <template v-else>
    <Card v-if="balance"><p>{{ t('okr.personalWallet') }} · {{ t('okr.available') }} {{ balance.available }} · {{ t('okr.held') }} {{ balance.held }}</p></Card>
    <WorkSyncSetup v-if="detail === null" :key="currentGuild.slug" />
    <Card v-if="kr_options.length > 0 && detail === null"><label>{{ t('okr.claimKr') }}<Select :disabled="busy" :aria-label="t('okr.claimKr')" v-model="kr" :options="[{value: '', label: t('okr.chooseKr')}, ...kr_options]" /></label><Button :disabled="busy || kr === ''" @click="claimKr">{{ t('okr.claim') }}</Button></Card>
    <Card v-if="detail === null">
      <div class="work-actions"><Button :variant="view === 'mine' ? 'primary' : 'ghost'" @click="view = 'mine'">{{ t('okr.mine') }}</Button><Button :variant="view === 'roots' ? 'primary' : 'ghost'" @click="view = 'roots'">{{ t('okr.allRoots') }}</Button></div>
      <p v-if="rows.length === 0">{{ t('okr.empty') }}</p>
      <ListToolbar :view="listView" /><p v-if="rows.length > 0 && listView.shown.value.length === 0">{{ t('list.noMatch') }}</p>
      <ul class="work-list"><li v-for="item in listView.shown.value" :key="item.id"><router-link :to="`/work/${item.id}`">{{ item.title }}</router-link><span>{{ person(item.holder_user_id) }} · {{ t(`okr.status.${item.status}`) }} · {{ item.reward }}</span></li></ul>
      <Button v-if="next_before !== null" variant="ghost" @click="perform(() => loadRows(true))">{{ t('okr.more') }}</Button>
    </Card>
    <template v-else>
      <nav class="work-actions"><router-link to="/work">{{ t('okr.mine') }}</router-link><router-link v-if="task.parent_task_id !== null" :to="`/work/${task.parent_task_id}`">{{ t('okr.parent') }}</router-link></nav>
      <Card>
        <Badge>{{ t(`okr.status.${task.status}`) }}</Badge><h2>{{ task.title }}</h2><p class="work-copy">{{ task.spec }}</p>
        <p>{{ t('okr.owner') }}: {{ person(task.holder_user_id) }} · {{ t('okr.publisher') }}: {{ person(task.publisher_user_id) }} · {{ t('okr.reward') }}: {{ task.reward }}</p>
        <ul v-if="task.acceptance.length > 0"><li v-for="item in task.acceptance" :key="item">{{ item }}</li></ul>
        <div class="work-actions"><Button v-if="task.status === 'open'" :disabled="busy" @click="action('claim')">{{ t('okr.claim') }}</Button><Button v-if="mine && ['claimed','rework'].includes(task.status)" @click="router.push(`/work/new?parent=${task.id}`)">{{ t('okr.newChild') }}</Button><Button v-if="publisher && ['open','claimed','rework'].includes(task.status)" variant="secondary" @click="router.push(`/work/${task.id}/edit`)">{{ t('okr.edit') }}</Button></div>
        <section v-if="publisher && !['settled','cancelled'].includes(task.status)" class="work-form">
          <label>{{ t('okr.owner') }}<Select :disabled="busy" :aria-label="t('okr.owner')" v-model="chosen_owner" :options="ownerOptions" /></label>
          <div class="work-actions"><Button :disabled="busy || chosen_owner === ''" @click="action('assign', {holder_user_id: chosen_owner})">{{ t('okr.assign') }}</Button><Button variant="secondary" :disabled="busy || task.status === 'open'" @click="action('release')">{{ t('okr.release') }}</Button><Button variant="ghost" :disabled="busy" @click="action('cancel')">{{ t('okr.cancelTask') }}</Button></div>
        </section>
        <Button v-if="mine && !publisher && ['claimed','rework','submitted'].includes(task.status)" variant="ghost" :disabled="busy" @click="action('release')">{{ t('okr.abandon') }}</Button>
        <section v-if="mine && ['claimed','rework'].includes(task.status)" class="work-form">
          <label>{{ t('okr.executor') }}<Select :disabled="busy" :aria-label="t('okr.executor')" v-model="chosen_executor" :options="agentOptions" /></label><Button variant="secondary" :disabled="busy" @click="action('delegate', {executor_agent_id: chosen_executor === '' ? null : chosen_executor})">{{ t('okr.delegate') }}</Button>
          <label>{{ t('okr.result') }}<Textarea :disabled="busy" v-model="summary" /></label>
          <label>{{ t('okr.link') }}<Input :disabled="busy" v-model="link" type="url" /></label>
          <label>{{ t('okr.file') }}<input type="file" :disabled="busy" @change="attach"></label><p v-if="artifacts.length">{{ t('okr.filesAdded', {n: artifacts.length}) }}</p>
          <Button :disabled="busy || summary.trim() === ''" @click="action('submit', {summary, artifacts: link.trim() === '' ? artifacts : [...artifacts, {type:'link', url:link.trim()}]})">{{ t('okr.submit') }}</Button>
        </section>
        <section v-if="publisher && task.status === 'submitted'" class="work-form">
          <label>{{ t('okr.rejectionReason') }}<Textarea :disabled="busy" v-model="reason" /></label>
          <div class="work-actions"><Button :disabled="busy" @click="action('accept')">{{ t('okr.accept') }}</Button><Button variant="secondary" :disabled="busy || reason.trim() === ''" @click="action('reject', {reason})">{{ t('okr.reject') }}</Button></div>
        </section>
        <div v-if="(mine || publisher) && ['submitted','rework'].includes(task.status)" class="work-actions"><Button variant="ghost" :disabled="busy" @click="action('remind')">{{ t('okr.remind') }}</Button><Button variant="ghost" :disabled="busy" @click="action('escalate')">{{ t('okr.escalate') }}</Button></div>
      </Card>
      <Card v-if="synchronization !== null && synchronization.binding !== null">
        <h2>{{ t('okr.sync.title') }}</h2>
        <p>{{ t(`okr.sync.status.${synchronization.binding.status}`) }}</p>
        <p>{{ t('okr.sync.explanation') }}</p>
        <p v-if="synchronization.last_error !== null" role="alert">{{ synchronization.last_error }}</p>
        <p v-if="task.source_state === 'source_deleted'" role="alert">{{ t('okr.sync.sourceDeleted') }}</p>
        <section v-if="synchronization.binding.conflict !== null" class="work-form">
          <h3>{{ t('okr.sync.conflict') }}</h3>
          <ul><li v-for="field in synchronization.binding.conflict.fields" :key="field">{{ t(`okr.sync.fields.${field}`) }}</li></ul>
          <p class="work-copy">{{ t('okr.sync.platform') }}: {{ synchronization.binding.conflict.platform.title }} · {{ t(`okr.status.${synchronization.binding.conflict.platform.status}`) }} · {{ synchronization.binding.conflict.platform.holder_user_id }}</p>
          <p class="work-copy">{{ t('okr.sync.remote') }}: {{ synchronization.binding.conflict.remote.title }} · {{ synchronization.binding.conflict.remote.completion }}% · {{ synchronization.binding.conflict.remote.holder_user_id }}</p>
          <div v-if="publisher" class="work-actions">
            <Button :disabled="busy" @click="synchronize('resolve', {conflict_id: synchronization.binding.conflict.id, side:'platform'})">{{ t('okr.sync.keepPlatform') }}</Button>
            <Button variant="secondary" :disabled="busy" @click="synchronize('resolve', {conflict_id: synchronization.binding.conflict.id, side:'feishu'})">{{ t('okr.sync.useFeishu') }}</Button>
          </div>
        </section>
      </Card>
      <Card><h2>{{ t('okr.children') }}</h2><p>{{ t('okr.independentChildren') }}</p><ul class="work-list"><li v-for="child in children" :key="child.id"><router-link :to="`/work/${child.id}`">{{ child.title }}</router-link><span>{{ person(child.holder_user_id) }} · {{ t(`okr.status.${child.status}`) }}</span></li></ul><Button v-if="child_cursor !== null" variant="ghost" @click="perform(() => loadChildren(true))">{{ t('okr.more') }}</Button></Card>
      <Card><h2>{{ t('okr.submissions') }}</h2><article v-for="submission in detail.submissions" :key="submission.id" class="work-entry"><h3>{{ t('okr.round', {n:submission.round}) }} · {{ person(submission.user_id) }}</h3><p class="work-copy">{{ submission.summary }}</p><ul><li v-for="(artifact, index) in submission.artifacts" :key="index"><a v-if="artifact.type === 'link'" :href="artifact.url" target="_blank" rel="noopener">{{ artifact.url }}</a><Button v-else variant="ghost" @click="download(artifact.file_id)">{{ t('okr.file') }} {{ index + 1 }}</Button></li></ul></article></Card>
      <Card><h2>{{ t('okr.history') }}</h2><article v-for="entry in detail.activities" :key="entry.id" class="work-entry"><strong>{{ t(`okr.activity.${entry.type}`) }} · {{ person(entry.actor_user_id) }}</strong><time>{{ date(entry.created_at) }}</time><p v-if="['assign','release','claim'].includes(entry.type)">{{ t('okr.owner') }}: {{ person(entry.payload.previous_holder_user_id) }} → {{ person(entry.payload.holder_user_id) }}</p></article></Card>
      <Card><h2>{{ t('okr.reviews') }}</h2><article v-for="review in detail.reviews" :key="review.id" class="work-entry"><strong>{{ t(`okr.${review.action}`) }} · {{ person(review.reviewer_user_id) }}</strong><p>{{ review.reason }}</p></article></Card>
    </template>
    <Card v-if="notices.length > 0"><h2>{{ t('okr.notifications') }}</h2><ul class="work-list"><li v-for="notice in notices" :key="notice.id"><router-link :to="`/work/${notice.task_id}`">{{ t(`okr.activity.${notice.type}`) }}</router-link><time>{{ date(notice.created_at) }}</time></li></ul></Card>
  </template>
  </section>
</template>

<style scoped>
.work-page { display:grid; gap:var(--drama-space-lg); min-width:0; }
.work-page > * { min-width:0; }
.work-header,.work-actions { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:var(--drama-space-md); }
.work-actions { justify-content:flex-start; margin-block:var(--drama-space-md); }
.work-form { display:grid; gap:var(--drama-space-md); margin-block:var(--drama-space-lg); }
.work-form label { display:grid; gap:var(--drama-space-sm); }
.work-list { list-style:none; padding:0; }
.work-list li { display:flex; justify-content:space-between; gap:var(--drama-space-md); flex-wrap:wrap; padding-block:var(--drama-space-md); border-bottom:1px solid var(--drama-color-border); }
.work-list span,.work-header p { color:var(--drama-color-text-muted); }
.work-entry > time { display:block; color:var(--drama-color-text-muted); }
.work-copy { white-space:pre-wrap; overflow-wrap:anywhere; }
.work-entry { border-top:1px solid var(--drama-color-border); padding-block:var(--drama-space-md); }
</style>
